/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import * as ApiService from 'common/services/ApiService';

function getDistributionCenters(route, query) {
    return ApiService.get(true, route, query);
}

function getEhubDistributionCenters(country) {
    return ApiService.get(true, 'VendorDistributionCenters/ehubs', { country });
}

function getDistributionCenterCount(filteredQuery) {
    const query = {
        where: {
            and: [
                {
                    documentTypeId: 'VENDORDISTRIBUTIONCENTER',
                },
                {
                    active: true,
                },
            ],
        },
    };
    if (filteredQuery !== undefined) {
        query.where = filteredQuery;
    }
    return ApiService.post(
        true,
        'VendorDistributionCenters/search/count',
        query
    );
}

function getDistributionCenter(id, query) {
    return ApiService.get(true, `VendorDistributionCenters/${id}`, query);
}

function getDistributionCenterSuggestions(query, tradeTypeId, country) {
    const _query = {
        prefix: query,
    };
    if (tradeTypeId) {
        _query.tradeTypeId = tradeTypeId;
    }
    if (country) {
        _query.country = country;
    }
    return ApiService.get(
        true,
        'VendorDistributionCenters/suggestions',
        _query
    );
}

function getFilterSearchable() {
    return {
        vendorBranchNumber: {
            id: 'vendorBranchNumber',
            accessor: 'vendorBranchNumber',
            display: 'Branch Number',
            route: 'VendorDistributionCenters',
            query: term => {
                // eslint-disable-line arrow-body-style
                return {
                    filter: {
                        limit: 10,
                        where: {
                            vendorBranchNumber: {
                                like: `%${term}%`,
                            },
                        },
                        fields: {
                            vendorBranchNumber: true,
                            id: true,
                        },
                    },
                };
            },
        },
        vendorBranchName: {
            id: 'vendorBranchName',
            accessor: 'vendorBranchName',
            display: 'Location Name',
            route: 'VendorDistributionCenters',
            query: term => {
                // eslint-disable-line arrow-body-style
                return {
                    filter: {
                        limit: 10,
                        where: {
                            vendorBranchName: {
                                like: `%${term}%`,
                            },
                        },
                        fields: {
                            vendorBranchName: true,
                            id: true,
                        },
                    },
                };
            },
        },
        stdAddress: {
            id: 'stdAddress',
            accessor: 'stdAddress',
            display: 'Address',
            route: 'VendorDistributionCenters',
            query: term => {
                // eslint-disable-line arrow-body-style
                return {
                    filter: {
                        limit: 10,
                        where: {
                            stdAddress: {
                                like: `%${term}%`,
                            },
                        },
                        fields: {
                            stdAddress: true,
                            id: true,
                        },
                    },
                };
            },
        },
        stdCity: {
            id: 'stdCity',
            accessor: 'stdCity',
            display: 'City',
            route: 'VendorDistributionCenters',
            query: term => {
                // eslint-disable-line arrow-body-style
                return {
                    filter: {
                        limit: 5,
                        where: {
                            stdCity: {
                                like: `%${term}%`,
                            },
                        },
                        fields: {
                            stdCity: true,
                            id: true,
                        },
                    },
                };
            },
        },
        stdPostalCode: {
            id: 'stdPostalCode',
            accessor: 'stdPostalCode',
            display: 'Zip',
            route: 'VendorDistributionCenters',
            query: term => {
                // eslint-disable-line arrow-body-style
                return {
                    filter: {
                        limit: 10,
                        where: {
                            stdPostalCode: {
                                like: `%${term}%`,
                            },
                        },
                        fields: {
                            stdPostalCode: true,
                            id: true,
                        },
                    },
                };
            },
        },
    };
}

function getStates() {
    return ApiService.get(true, 'UsStates').then(res =>
        res.map(item => ({ display: item.display, term: item.id }))
    );
}

function getAdminDistributionCenterColumns() {
    return [
        {
            header: 'Branch Number',
            accessor: 'vendorBranchNumber',
            sortable: true,
            order: null,
        },
        {
            header: 'Location Name',
            accessor: 'vendorBranchName',
            sortable: false,
        },
        {
            header: 'Address',
            accessor: 'stdAddress',
            sortable: false,
        },
        {
            header: 'City',
            accessor: 'stdCity',
            sortable: true,
            order: null,
        },
        {
            header: 'State',
            accessor: 'stdRegion',
            sortable: true,
            order: null,
        },
        {
            header: 'Zip',
            accessor: 'stdPostalCode',
            sortable: false,
        },
    ];
}

function processFilters(
    activeFilters,
    userId,
    sortOrder,
    columnAccessor,
    skipQuantity
) {
    const query = {
        filter: {
            order: 'vendorBranchNumber ASC',
            limit: 30,
            skip: 0,
            where: {
                and: [
                    {
                        documentTypeId: 'VENDORDISTRIBUTIONCENTER',
                    },
                    {
                        active: true,
                    },
                ],
            },
            include: [
                {
                    relation: 'tradeTypes',
                },
            ],
        },
    };

    if (sortOrder && columnAccessor !== undefined) {
        query.filter.order = `${columnAccessor} ${sortOrder}`;
    }

    if (skipQuantity !== undefined) {
        query.filter.skip = skipQuantity;
    }

    if (!activeFilters.length) {
        return query;
    }

    const stateQuickFilter = { or: [] };

    activeFilters.forEach(filter => {
        switch (filter.target) {
            //     // Search Filters
            case 'vendorBranchNumber':
                query.filter.where.and.push({
                    vendorBranchNumber: filter.term,
                });
                break;
            case 'vendorBranchName':
                query.filter.where.and.push({ vendorBranchName: filter.term });
                break;
            case 'stdAddress':
                query.filter.where.and.push({ stdAddress: filter.term });
                break;
            case 'stdCity':
                query.filter.where.and.push({ stdCity: filter.term });
                break;
            case 'stdPostalCode':
                query.filter.where.and.push({ stdPostalCode: filter.term });
                break;
            //     // Quick Filters
            case 'tradeTypes':
                stateQuickFilter.or.push({ stdRegion: filter.term });
                break;
            default:
        }
    });

    query.filter.where.and.push(stateQuickFilter);

    return query;
}

export {
    getEhubDistributionCenters,
    getDistributionCenters,
    getDistributionCenter,
    getDistributionCenterCount,
    getDistributionCenterSuggestions,
    processFilters,
    getFilterSearchable,
    getAdminDistributionCenterColumns,
    getStates,
};
